export const SYSTEM_FONT_FAMILIES: string[] = [
  "Arial",
  "Helvetica",
  "Times New Roman",
  "Courier New",
  "Verdana",
  "Georgia",
  "Palatino",
  "Garamond",
  "Comic Sans MS",
  "Trebuchet MS",
  "Impact",
  "Lucida Console",
  "Tahoma",
];

// These are the fonts your UI exposes as selectable “Google Fonts”.
export const GOOGLE_FONT_FAMILIES: string[] = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Oswald",
  "Raleway",
  "Poppins",
  "Source Sans Pro",
  "Playfair Display",
  "Merriweather",
  "Ubuntu",
  "Bebas Neue",
  "Bangers",
  "Righteous",
  "Fredoka One",
  "Comfortaa",
  "Quicksand",
  "Kalam",
  "Permanent Marker",
  "Indie Flower",
  "Crimson Text",
  "Fira Sans",
  "PT Sans",
  "Nunito",
  "Work Sans",
  "Rubik",
  "Noto Sans",
  "Mukta",
  "DM Sans",
  "Manrope",
  "Outfit",
  "Plus Jakarta Sans",
  // Arabic fonts
  "Amiri",
  "Changa",
  "El Messiri",
  "Harmattan",
  "Lateef",
  "Markazi Text",
  "Reem Kufi",
  "Scheherazade New",
  "Noto Sans Arabic",
  "Noto Naskh Arabic",
  "Noto Kufi Arabic",
  "IBM Plex Sans Arabic",
  "Cairo",
  "Tajawal",
  "Almarai",
];

// Fonts that are provided as local three.js typeface jsons / public assets.
export const TYPEFACE_FONT_FAMILIES: string[] = [
  "Almarai",
  "Anton",
  "Barlow Condensed",
  "BBH Bartle",
  "Cairo",
  "Caveat",
  "Dancing Script",
  "Edu SA Beginner",
  "Follow",
  "Great Vibes",
  "Josefin Sans Thin",
  "Lobster",
  "Lobster Two",
  "Noto Sans Symbols",
  "Orbitron",
  "Pacifico",
  "Satisfy",
  "Shadows Into Light",
  "Smooch Sans Thin",
  "Space Grotesk Light",
  "Tajawal",
];

export const getAllFontFamilies = (): string[] => {
  return Array.from(
    new Set([
      ...SYSTEM_FONT_FAMILIES,
      ...GOOGLE_FONT_FAMILIES,
      ...TYPEFACE_FONT_FAMILIES,
    ])
  ).sort();
};

export const sanitizeFontName = (fontName: string): string => {
  return String(fontName || "")
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .toLowerCase();
};

export const createSanitizedFontMap = (): Map<string, string> => {
  const map = new Map<string, string>();
  getAllFontFamilies().forEach((font) => {
    map.set(sanitizeFontName(font), font);
  });
  return map;
};

const extractInlineFontFamilies = (html: string): string[] => {
  // Matches inline styles: font-family: "Open Sans", Arial;
  const matches = html.match(/font-family\s*:\s*([^;"']+|"[^"]+"|'[^']+')/gi) || [];
  const families: string[] = [];

  matches.forEach((m) => {
    const value = m.replace(/font-family\s*:\s*/i, "").trim();
    const first = value.split(",")[0]?.trim();
    const unquoted = first?.replace(/^["']|["']$/g, "");
    if (unquoted) families.push(unquoted);
  });

  return families;
};

export const extractFontFamiliesFromHtml = (
  html: string,
  sanitizedFontMap: Map<string, string>
): string[] => {
  if (!html) return [];

  // 1) Quill classes: ql-font-<sanitized>
  const classMatches = html.match(/ql-font-([a-z0-9-]+)/gi) || [];
  const classFamilies = classMatches
    .map((m) => m.replace(/^ql-font-/i, "").toLowerCase())
    .map((key) => sanitizedFontMap.get(key))
    .filter((v): v is string => typeof v === "string" && v.length > 0);

  // 2) Inline styles
  const inlineFamilies = extractInlineFontFamilies(html);

  return Array.from(new Set([...classFamilies, ...inlineFamilies]));
};

